/**
 * Topbar Tasks dropdown — three sections (Overdue / Today / Upcoming)
 * with inline checkbox completion + a "+ New task" affordance that
 * opens the full task modal on the page.
 */

import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { CheckSquare, Plus } from 'lucide-react'
import type { ReactNode } from 'react'
import { get, post } from '@/lib/api'
import { Skeleton } from '@/components/ui/Skeleton'
import { NewTaskModal } from './NewTaskModal'

interface Task {
  id: string
  title: string
  status: string
  priority: 'low' | 'medium' | 'high' | 'urgent'
  dueDate: string | null
  entityType?: string | null
  entityId?: string | null
}

interface MyTasksResp {
  overdue: Task[]
  dueToday: Task[]
  upcoming: Task[]
  completed: Task[]
}

export function TasksPanel({ dark = true }: { dark?: boolean } = {}) {
  const [open, setOpen] = useState(false)
  const [showNew, setShowNew] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const qc = useQueryClient()

  const tasks = useQuery({
    queryKey: ['tasks', 'my-tasks'],
    queryFn: () => get<MyTasksResp>('/api/v1/tasks/my-tasks', { background: true }),
    refetchInterval: 60_000,
    staleTime: 15_000,
  })

  const overdue = tasks.data?.overdue.length ?? 0
  const total = (tasks.data?.overdue.length ?? 0)
    + (tasks.data?.dueToday.length ?? 0)
    + (tasks.data?.upcoming.length ?? 0)

  useEffect(() => {
    if (!open) return
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [open])

  const complete = useMutation({
    mutationFn: (id: string) => post(`/api/v1/tasks/${id}/complete`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tasks'] })
    },
    onError: (e: Error) => toast.error(e.message),
  })

  const idleColor = dark ? 'rgba(255,255,255,0.55)' : '#64748B'
  const hoverBg = dark ? 'rgba(255,255,255,0.08)' : '#F8FAFC'
  const badgeColor = overdue > 0 ? '#E11D48' : '#2B8FD4'

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="Tasks"
        className="relative inline-flex h-9 w-9 items-center justify-center rounded-md transition-colors"
        style={{ color: idleColor }}
        onMouseEnter={(e) => { e.currentTarget.style.background = hoverBg }}
        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent' }}
      >
        <CheckSquare style={{ height: 20, width: 20 }} />
        {total > 0 ? (
          <span
            className="absolute flex items-center justify-center font-bold text-white"
            style={{
              top: -2, right: -2,
              height: 16, width: 16, minWidth: 16,
              borderRadius: '50%',
              background: badgeColor,
              fontSize: 10,
            }}
          >
            {total > 9 ? '9+' : total}
          </span>
        ) : null}
      </button>

      {open ? (
        <div
          className="absolute right-0 z-50 bg-white overflow-hidden"
          style={{
            top: 'calc(100% + 8px)',
            width: 360,
            maxHeight: 480,
            border: '1px solid #e2e8f0',
            borderRadius: 14,
            boxShadow: '0 8px 30px rgba(0,0,0,0.10)',
          }}
        >
          <div className="px-4 py-3 border-b border-[#e2e8f0] flex items-center justify-between">
            <p className="text-sm font-bold text-[#0f172a]">My Tasks · {total}</p>
            <button
              onClick={() => { setShowNew(true); setOpen(false) }}
              className="inline-flex items-center gap-1 bg-[#2B8FD4] text-white rounded-md px-2.5 py-1 text-xs font-semibold hover:bg-[#1a7bc4]"
            >
              <Plus className="h-3 w-3" /> New
            </button>
          </div>

          <div className="overflow-y-auto" style={{ maxHeight: 400 }}>
            {tasks.isLoading ? (
              <div className="p-4"><Skeleton className="h-24 w-full" /></div>
            ) : total === 0 ? (
              <div className="px-4 py-12 text-center">
                <p className="text-sm font-medium text-[#0f172a]">All clear</p>
                <p className="text-xs text-[#94a3b8] mt-1">No open tasks</p>
              </div>
            ) : (
              <>
                {(tasks.data?.overdue.length ?? 0) > 0 ? (
                  <Section label="Overdue" color="#E11D48" count={tasks.data!.overdue.length}>
                    {tasks.data!.overdue.map((t) => (
                      <TaskRow key={t.id} task={t} onComplete={() => complete.mutate(t.id)} accent="#E11D48" />
                    ))}
                  </Section>
                ) : null}
                {(tasks.data?.dueToday.length ?? 0) > 0 ? (
                  <Section label="Due today" color="#D97706" count={tasks.data!.dueToday.length}>
                    {tasks.data!.dueToday.map((t) => (
                      <TaskRow key={t.id} task={t} onComplete={() => complete.mutate(t.id)} accent="#D97706" />
                    ))}
                  </Section>
                ) : null}
                {(tasks.data?.upcoming.length ?? 0) > 0 ? (
                  <Section label="Upcoming" color="#2B8FD4" count={tasks.data!.upcoming.length}>
                    {tasks.data!.upcoming.slice(0, 5).map((t) => (
                      <TaskRow key={t.id} task={t} onComplete={() => complete.mutate(t.id)} accent="#2B8FD4" />
                    ))}
                  </Section>
                ) : null}
              </>
            )}
          </div>

          <div className="px-4 py-2 border-t border-[#e2e8f0] text-center">
            <Link
              to="/tasks"
              onClick={() => setOpen(false)}
              className="text-xs text-[#2B8FD4] font-semibold hover:underline"
            >
              View all tasks →
            </Link>
          </div>
        </div>
      ) : null}

      <NewTaskModal
        open={showNew}
        onClose={() => setShowNew(false)}
        onCreated={() => qc.invalidateQueries({ queryKey: ['tasks'] })}
      />
    </div>
  )
}

function Section({ label, color, count, children }: { label: string; color: string; count: number; children: ReactNode }) {
  return (
    <div>
      <div className="px-4 py-2 bg-[#f8fafc] flex items-center gap-2">
        <span className="rounded-full" style={{ width: 6, height: 6, background: color }} />
        <p className="text-[10px] font-semibold tracking-wider uppercase" style={{ color }}>
          {label} · {count}
        </p>
      </div>
      {children}
    </div>
  )
}

function TaskRow({ task, onComplete, accent }: { task: Task; onComplete: () => void; accent: string }) {
  const due = task.dueDate ? new Date(task.dueDate) : null
  return (
    <div className="px-4 py-3 border-b border-[#f1f5f9] flex items-start gap-3 hover:bg-[#f8fafc]">
      <button
        onClick={(e) => { e.stopPropagation(); onComplete() }}
        aria-label="Complete task"
        className="w-5 h-5 rounded border-2 border-[#e2e8f0] mt-0.5 shrink-0 hover:border-[#2B8FD4] hover:bg-[#EFF6FF] transition-colors"
      />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-[#0f172a] truncate">{task.title}</p>
        {task.entityType && task.entityId ? (
          <Link to={`/${task.entityType}s/${task.entityId}`} className="text-xs text-[#2B8FD4] hover:underline">
            {task.entityType[0]!.toUpperCase() + task.entityType.slice(1)} link
          </Link>
        ) : null}
        {due ? (
          <p className="text-[11px] mt-0.5" style={{ color: accent }}>
            {due.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
          </p>
        ) : null}
      </div>
      <span
        className="rounded-full mt-1.5"
        style={{
          width: 8, height: 8,
          background: task.priority === 'urgent' ? '#E11D48'
            : task.priority === 'high' ? '#D97706'
              : task.priority === 'medium' ? '#2B8FD4' : '#94a3b8',
        }}
      />
    </div>
  )
}
